import React from "react";
import { Link } from "react-router-dom";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Avatar, Kicker } from "@/components/kit";
import { ROMAN, fmtDate, statusColor } from "@/lib/util";
import { ArrowUpRight, Check, Minus } from "lucide-react";

function Register() {
  const { meetings, activeMembers, me, canWrite, refresh } = useTable();
  const isAdmin = me.isAdmin && canWrite;
  if (meetings.length === 0 || activeMembers.length === 0) return null;

  const toggle = async (meeting, memberId) => {
    if (!isAdmin) return;
    const present = meeting.presentMemberIds || [];
    const next = present.includes(memberId)
      ? present.filter((x) => x !== memberId)
      : [...present, memberId];
    try {
      await api.put(`/meetings/${meeting.id}`, { presentMemberIds: next });
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const held = meetings.filter((m) => m.status === "Done");

  return (
    <Card className="p-5 mb-6" data-testid="attendance-register">
      <Kicker className="!text-[9px]">Attendance register</Kicker>
      <p className="text-xs mt-2 mb-4" style={{ color: "var(--ink-mute)" }}>
        Who sat at the table, chapter by chapter{isAdmin ? " — tap a cell to mark present" : ""}.
        Absences from a closed chapter are flagged for a policy fine on the Fines page.
      </p>
      <div className="overflow-x-auto no-scrollbar -mx-1 px-1">
        <table className="w-full text-sm" style={{ borderCollapse: "collapse", minWidth: 430 }}>
          <thead>
            <tr>
              <th className="text-left kicker !text-[9px] pb-2 pr-3 whitespace-nowrap">Member</th>
              {meetings.map((m) => (
                <th key={m.id} className="roman text-lg font-normal pb-2 px-1" style={{ opacity: m.status === "Done" ? 1 : 0.4 }}>
                  {ROMAN[m.num - 1]}
                </th>
              ))}
              <th className="kicker !text-[9px] pb-2 pl-3">Held</th>
            </tr>
          </thead>
          <tbody>
            {activeMembers.map((mem) => {
              const attended = held.filter((m) => (m.presentMemberIds || []).includes(mem.id)).length;
              return (
                <tr key={mem.id} className="border-t" style={{ borderColor: "var(--hair)" }} data-testid={`register-row-${mem.id}`}>
                  <td className="py-2.5 pr-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <Avatar name={mem.name} color={mem.avatarColor} size={22} />
                      <span className="sm:hidden">{mem.name.split(" ")[0]}</span>
                      <span className="hidden sm:inline">{mem.name}</span>
                    </span>
                  </td>
                  {meetings.map((m) => {
                    const present = (m.presentMemberIds || []).includes(mem.id);
                    const closed = m.status === "Done";
                    return (
                      <td key={m.id} className="text-center px-1">
                        <button
                          data-testid={`register-cell-${m.num}-${mem.id}`}
                          disabled={!isAdmin}
                          onClick={() => toggle(m, mem.id)}
                          title={`Chapter ${ROMAN[m.num - 1]} · ${present ? "Present" : "Absent"}`}
                          className="inline-flex items-center justify-center rounded-full"
                          style={{
                            width: 26, height: 26,
                            background: present ? "var(--accent-wash)" : "transparent",
                            border: `1px solid ${present ? "var(--brass)" : "var(--hair)"}`,
                            color: present ? "var(--brass)" : closed ? "var(--rust)" : "var(--ink-mute)",
                            cursor: isAdmin ? "pointer" : "default",
                            transition: "background-color .2s var(--ease), border-color .2s var(--ease)",
                          }}
                        >
                          {present ? <Check size={13} /> : <Minus size={13} />}
                        </button>
                      </td>
                    );
                  })}
                  <td className="text-center pl-3 whitespace-nowrap" data-testid={`register-count-${mem.id}`}>
                    {attended}/{held.length}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Chapters() {
  const { meetings, memberById, year } = useTable();
  const next = meetings.find((m) => m.status !== "Done");

  return (
    <div data-testid="chapters-page">
      <PageHead
        kicker={`${year?.id || ""} · Eight sittings`}
        title="Chapters"
        sub="Eight sittings a year. The calendar is the discipline."
      />
      <Register />
      {meetings.length === 0 ? (
        <Empty line="No chapters set. The year begins with a date." />
      ) : (
        <div className="card-t !p-0 overflow-hidden divide-hair stagger">
          {meetings.map((m) => {
            const host = memberById(m.deepDiveHostId);
            const isNext = next && m.id === next.id;
            const done = m.status === "Done";
            return (
              <Link
                key={m.id}
                to={`/chapters/${m.id}`}
                data-testid={`chapter-card-${m.num}`}
                className="group flex gap-5 sm:gap-8 items-start p-6 sm:p-8 relative"
                style={{ transition: "background-color .25s var(--ease)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(37,99,235,.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {isNext && (
                  <span
                    className="absolute left-0 top-0 bottom-0"
                    style={{ width: 2, background: "var(--brass-bright)" }}
                  />
                )}
                <span
                  className="roman text-[2.75rem] sm:text-[3.75rem] w-14 sm:w-24 shrink-0 text-center"
                  style={{ opacity: done ? 1 : 0.42 }}
                >
                  {ROMAN[m.num - 1]}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="serif text-2xl sm:text-[1.7rem] leading-tight">{m.title || "Untitled"}</h2>
                  <p className="text-sm mt-2" style={{ color: m.date ? "var(--ink-mute)" : "var(--rust)" }}>
                    {m.date ? fmtDate(m.date) : "No date yet"}
                    {m.time ? ` · ${m.time}` : ""}
                    {m.venue ? ` · ${m.venue}` : ""}
                  </p>
                  <div className="mt-4 flex gap-2 flex-wrap items-center">
                    <Chip>{m.type}</Chip>
                    <Chip color={statusColor(m.status)}>{m.status}</Chip>
                    {isNext && <Chip color="var(--brass)">Next</Chip>}
                    {host && (
                      <span className="chip !pl-1">
                        <Avatar name={host.name} color={host.avatarColor} size={18} />
                        Deep dive · {host.name}
                      </span>
                    )}
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="shrink-0 mt-2 opacity-0 group-hover:opacity-60"
                  style={{ color: "var(--brass)", transition: "opacity .25s var(--ease)" }}
                />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
